import { useShow } from '@refinedev/core';
import { Card, Col, Descriptions, Row, Space, Table, Tag, Typography, Select, Button, message } from 'antd';
import { ArrowLeftOutlined, EnvironmentOutlined, UserOutlined, CarOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { apiClient } from '../../lib/api-client';

const { Title, Text } = Typography;

const tripModeLabels: Record<string, string> = {
  SHAHARLAR_ARO: 'Shaharlar aro',
  VILOYATLAR_ARO: 'Viloyatlar aro',
};

const statusLabels: Record<string, string> = {
  ACTIVE: 'Faol',
  FULL: "To'liq",
  DEPARTED: "Yo'lda",
  COMPLETED: 'Yakunlangan',
  CANCELLED: 'Bekor qilingan',
};

const statusColors: Record<string, string> = {
  ACTIVE: 'blue',
  FULL: 'purple',
  DEPARTED: 'orange',
  COMPLETED: 'green',
  CANCELLED: 'red',
};

const bookingStatusLabels: Record<string, string> = {
  PENDING: 'Kutilmoqda',
  CONFIRMED: 'Tasdiqlangan',
  CANCELLED: 'Bekor qilingan',
  COMPLETED: 'Yakunlangan',
  ON_THE_WAY: "Yo'lda",
  ARRIVED: 'Yetib keldi',
  IN_PROGRESS: 'Jarayonda',
};

const bookingStatusColors: Record<string, string> = {
  PENDING: 'gold',
  CONFIRMED: 'blue',
  CANCELLED: 'red',
  COMPLETED: 'green',
  ON_THE_WAY: 'orange',
  ARRIVED: 'cyan',
  IN_PROGRESS: 'purple',
};

const formatPrice = (price: number) => {
  return new Intl.NumberFormat('uz-UZ').format(price) + " so'm";
};

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('uz-UZ', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const DriverPostedTripShow = () => {
  const navigate = useNavigate();
  const { queryResult } = useShow({ resource: 'admin/driver-posted-trips' });
  const { data, isLoading } = queryResult;
  const trip = data?.data as any;
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const handleStatusUpdate = async (newStatus: string) => {
    setUpdatingStatus(true);
    try {
      await apiClient.put(`/admin/driver-posted-trips/${trip.id}`, { status: newStatus });
      message.success('Holat yangilandi');
      queryResult.refetch();
    } catch {
      message.error('Xatolik yuz berdi');
    } finally {
      setUpdatingStatus(false);
    }
  };

  if (isLoading || !trip) {
    return <Card loading />;
  }

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/driver-posted-trips')}>
          Orqaga
        </Button>
        <Title level={4} style={{ margin: 0 }}>
          Safar #{trip.id}
        </Title>
        <Tag color={statusColors[trip.status] || 'default'} style={{ fontSize: 14, padding: '2px 12px' }}>
          {statusLabels[trip.status] || trip.status}
        </Tag>
        <Tag color={trip.tripMode === 'SHAHARLAR_ARO' ? 'blue' : 'purple'}>
          {tripModeLabels[trip.tripMode] || trip.tripMode}
        </Tag>
      </Space>

      <Row gutter={[16, 16]}>
        {/* Left column */}
        <Col lg={8} xs={24}>
          {/* Driver Info */}
          <Card title={<Space><UserOutlined /> Haydovchi</Space>} size="small" style={{ marginBottom: 16 }}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Ism">{trip.driver?.fullName || '-'}</Descriptions.Item>
              <Descriptions.Item label="Telefon">{trip.driver?.phone || '-'}</Descriptions.Item>
              <Descriptions.Item label="Reyting">
                {trip.driver?.rating ? Number(trip.driver.rating).toFixed(1) : '-'}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Vehicle Info */}
          {trip.vehicle && (
            <Card title={<Space><CarOutlined /> Avtomobil</Space>} size="small" style={{ marginBottom: 16 }}>
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Marka">{trip.vehicle.make} {trip.vehicle.model}</Descriptions.Item>
                <Descriptions.Item label="Raqam">{trip.vehicle.plate}</Descriptions.Item>
                <Descriptions.Item label="Rang">{trip.vehicle.color}</Descriptions.Item>
              </Descriptions>
            </Card>
          )}

          {/* Route Info */}
          <Card title={<Space><EnvironmentOutlined /> Marshrut</Space>} size="small" style={{ marginBottom: 16 }}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Qayerdan">
                <Space direction="vertical" size={0}>
                  <Text strong>{trip.departureRegion?.nameUz || '-'}</Text>
                  <Text type="secondary">{trip.departureDistrict?.nameUz || ''}</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>{trip.departureAddress || ''}</Text>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Qayerga">
                <Space direction="vertical" size={0}>
                  <Text strong>{trip.destinationRegion?.nameUz || '-'}</Text>
                  <Text type="secondary">{trip.destinationDistrict?.nameUz || ''}</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>{trip.destinationAddress || ''}</Text>
                </Space>
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Trip Details */}
          <Card title="Safar tafsilotlari" size="small" style={{ marginBottom: 16 }}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Jo'nash vaqti">{formatDate(trip.departureTime)}</Descriptions.Item>
              <Descriptions.Item label="Joylar">
                <Tag color={trip.availableSeats > 0 ? 'green' : 'red'}>
                  {trip.availableSeats}/{trip.totalSeats}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Narx (1 joy)">{formatPrice(trip.pricePerSeat)}</Descriptions.Item>
              {trip.distanceKm && (
                <Descriptions.Item label="Masofa">{Number(trip.distanceKm).toFixed(1)} km</Descriptions.Item>
              )}
              {trip.durationMinutes && (
                <Descriptions.Item label="Davomiyligi">{trip.durationMinutes} daqiqa</Descriptions.Item>
              )}
              <Descriptions.Item label="Yo'l-yo'lakay">
                <Tag color={trip.acceptsRoadside ? 'green' : 'default'}>
                  {trip.acceptsRoadside ? 'Ha' : "Yo'q"}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Pochta">
                <Tag color={trip.acceptsPostal ? 'green' : 'default'}>
                  {trip.acceptsPostal ? 'Ha' : "Yo'q"}
                </Tag>
              </Descriptions.Item>
              {trip.driverNote && (
                <Descriptions.Item label="Izoh">{trip.driverNote}</Descriptions.Item>
              )}
              <Descriptions.Item label="Yaratilgan">{formatDate(trip.createdAt)}</Descriptions.Item>
              {trip.departedAt && (
                <Descriptions.Item label="Jo'nagan">{formatDate(trip.departedAt)}</Descriptions.Item>
              )}
              {trip.completedAt && (
                <Descriptions.Item label="Yakunlangan">{formatDate(trip.completedAt)}</Descriptions.Item>
              )}
              {trip.cancelledAt && (
                <Descriptions.Item label="Bekor qilingan">{formatDate(trip.cancelledAt)}</Descriptions.Item>
              )}
            </Descriptions>
          </Card>

          {/* Status Update */}
          {!['COMPLETED', 'CANCELLED'].includes(trip.status) && (
            <Card title="Holatni o'zgartirish" size="small">
              <Select
                style={{ width: '100%', marginBottom: 8 }}
                placeholder="Yangi holat tanlang"
                loading={updatingStatus}
                onChange={handleStatusUpdate}
                options={[
                  { value: 'ACTIVE', label: 'Faol', disabled: trip.status === 'ACTIVE' },
                  { value: 'FULL', label: "To'liq", disabled: trip.status === 'FULL' },
                  { value: 'DEPARTED', label: "Yo'lda", disabled: trip.status === 'DEPARTED' },
                  { value: 'COMPLETED', label: 'Yakunlangan' },
                  { value: 'CANCELLED', label: 'Bekor qilingan' },
                ]}
              />
            </Card>
          )}
        </Col>

        {/* Right column - Bookings */}
        <Col lg={16} xs={24}>
          <Card title={`Buyurtmalar (${trip.bookings?.length || 0})`} size="small">
            <Table
              dataSource={trip.bookings || []}
              rowKey="id"
              size="small"
              pagination={false}
            >
              <Table.Column dataIndex="id" title="ID" width={60} />
              <Table.Column
                title="Yo'lovchi"
                render={(_, record: any) => (
                  <div>
                    <div style={{ fontWeight: 500 }}>{record.passenger?.fullName || '-'}</div>
                    <div style={{ fontSize: 12, color: '#999' }}>{record.passenger?.phone || '-'}</div>
                  </div>
                )}
              />
              <Table.Column dataIndex="seatsBooked" title="Joylar" width={70} />
              <Table.Column
                dataIndex="totalPrice"
                title="Summa"
                width={120}
                render={(price: number) => formatPrice(price)}
              />
              <Table.Column
                dataIndex="status"
                title="Holat"
                width={120}
                render={(status: string) => (
                  <Tag color={bookingStatusColors[status] || 'default'}>
                    {bookingStatusLabels[status] || status}
                  </Tag>
                )}
              />
              <Table.Column
                dataIndex="paymentMethod"
                title="To'lov"
                width={90}
                render={(method: string, record: any) => (
                  <Space direction="vertical" size={0}>
                    <Text>{method || '-'}</Text>
                    <Tag color={record.isPaid ? 'green' : 'orange'} style={{ fontSize: 11 }}>
                      {record.isPaid ? "To'langan" : "To'lanmagan"}
                    </Tag>
                  </Space>
                )}
              />
              <Table.Column
                dataIndex="createdAt"
                title="Sana"
                width={140}
                render={(date: string) => formatDate(date)}
              />
              <Table.Column
                title=""
                width={50}
                render={(_, record: any) => (
                  <Button
                    type="text"
                    size="small"
                    onClick={() => navigate(`/driver-trip-bookings/${record.id}`)}
                  >
                    Ko'rish
                  </Button>
                )}
              />
            </Table>
          </Card>
        </Col>
      </Row>
    </div>
  );
};
